import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import LoadingSpinnerLight from 'src/common/components/loading-spinner/light';
import { Block } from '../types';
import { LogoType, useLogoStore } from './store';
import { Nullable } from 'src/types/nullable.type';
import { getDomainAndExtension } from 'src/utils/get-domain-and-extension';
import classNames from 'classnames';
import { functionNoop } from 'src/utils/function/noop';
import { MagicRegularIcon, NeueCheckmarkStrongIcon, RightStrongIcon, UploadRegularIcon } from 'src/monet/icons';
import { isValidUrl } from 'src/utils/url';
import { CompanyInfo, RecipientSearchInput } from '../components/dashboard/recipient-search-input';
import { useBlockActions } from '../helpers/actions/use-block-actions.hook';
import { useGetActiveLogoStack } from './get-active-logo-stack.hook';
import { FileUploadForm } from '../components/file-upload-form';
import { ControlPanelToggleButton } from '../components/control-panel/toggle-button';
import { apiAddProspectLogo } from 'src/utils/journeyApi';

const LogoFetcher = ({
  onEnterUrlRef,
  onFetchSuccess,
}: {
  onFetchSuccess: (sources: string[]) => void;
  onEnterUrlRef: MutableRefObject<(url: string) => Promise<string[]>>;
}) => {
  const [selectedRecipient, setSelectedRecipient] = useState<Nullable<CompanyInfo>>(null);
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');
  const [fetching, setFetching] = useState(false);

  const fetchLogos = (value: string) => {
    if (value) {
      setFetching(true);
      onEnterUrlRef
        .current(value)
        .then(onFetchSuccess)
        .catch(functionNoop)
        .then(() => {
          setFetching(false);
        });
    }
  };

  const onSelectRecipient = async (recipient: CompanyInfo) => {
    setSelectedRecipient(recipient);
    if (recipient) {
      fetchLogos(recipient.domain);
    }
  };

  return (
    <div className='relative bg-neue-journey-bg-90 z-10 rounded-t-lg'>
      <RecipientSearchInput
        autoFocus={true}
        placeholder='Fetch logo from URL'
        onResetRecipient={functionNoop}
        onSelectRecipient={onSelectRecipient}
        onRecipientSearchQueryChange={setRecipientSearchQuery}
        hasSelectedRecipient={!!selectedRecipient}
      />
      {isValidUrl(recipientSearchQuery) && (
        <div className='absolute inset-y-0 right-0 px-4 flex items-center'>
          {fetching ? (
            <LoadingSpinnerLight />
          ) : (
            <button
              onClick={() => {
                fetchLogos(recipientSearchQuery);
              }}
              className='has-hover'
            >
              <RightStrongIcon />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ACCEPTED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];

export type ActivePanelTypes = Nullable<'auto' | 'custom'>;

export const PickLogoPanel = ({ type, block }: { type: Nullable<LogoType>; block: Block }) => {
  const [activePanel, setActivePanel] = useState<ActivePanelTypes>(null);
  const firstElementRef = useRef<Nullable<HTMLDivElement>>(null);
  const { fetchLogos, updateLogoBlock, recipientDomain } = useLogoStore((state) => ({
    fetchLogos: state.fetchLogos,
    updateLogoBlock: state.updateLogoBlock,
    recipientDomain: state.recipientDomain,
  }));
  const blockId = block.id;
  const { onLogoBlockUpdated, onUploadNewFile } = useBlockActions(block);

  const {
    selectedOrgLogo,
    selectedRecipientLogo,
    availableOrgLogos,
    customOrgLogos,
    availableRecipientLogos,
    customRecipientLogos,
  } = useGetActiveLogoStack(block);

  const isActivePanelAuto = activePanel && activePanel === 'auto';
  const availableLogos =
    type === 'org' ? [availableOrgLogos, customOrgLogos] : [availableRecipientLogos, customRecipientLogos];

  useEffect(() => {
    if (activePanel === null) {
      const sourceToSearch = type === 'org' ? selectedOrgLogo : selectedRecipientLogo;
      if (!sourceToSearch) {
        setActivePanel('auto');
        return;
      } else if (availableLogos[0].some((logo) => logo.includes(sourceToSearch))) {
        setActivePanel('auto');
        return;
      }
      setActivePanel('custom');
    }
  }, [availableLogos, activePanel]);

  const scrollListUp = () => {
    firstElementRef.current!.scrollIntoView({ behavior: 'smooth' });
  };

  const onEnterUrlRef = useRef(async (url: string) => {
    let returnValue = Promise.resolve([] as string[]);
    const domain = getDomainAndExtension(url);
    if (domain && type) {
      returnValue = fetchLogos(domain, type, blockId, null, true);
    }
    return returnValue;
  });

  const renderLogoPreview = (src: string, isChecked: boolean) => {
    return (
      <>
        <img src={src} alt='' className='h-full m-auto' />
        {isChecked && (
          <div className='absolute right-2 top-2'>
            <NeueCheckmarkStrongIcon />
          </div>
        )}
      </>
    );
  };

  const logoListToRender = availableLogos[isActivePanelAuto ? 0 : 1];

  return (
    <div className='w-[248px] rounded-lg bg-neue-journey-bg-90 text-neue-journey-fg flex flex-col pointer-events-auto'>
      <div className='relative flex flex-col gap-4 p-4'>
        <div className='flex gap-2 text-neue-journey-fg'>
          <ControlPanelToggleButton
            label='Auto'
            icon={<MagicRegularIcon />}
            selected={activePanel === 'auto'}
            onClick={() => {
              setActivePanel('auto');
            }}
          />
          <ControlPanelToggleButton
            label='Custom'
            icon={<UploadRegularIcon />}
            selected={activePanel === 'custom'}
            onClick={() => {
              setActivePanel('custom');
            }}
          />
        </div>
        {!isActivePanelAuto ? (
          <>
            <FileUploadForm
              onNewFile={async (file) => {
                const blobUrl = URL.createObjectURL(file);

                const payload =
                  type === 'org'
                    ? { orgLogoEnabled: true, selectedOrgLogo: blobUrl, customOrgLogos: [blobUrl, ...logoListToRender] }
                    : {
                        recipientLogoEnabled: true,
                        selectedRecipientLogo: blobUrl,
                        customRecipientLogos: [blobUrl, ...logoListToRender],
                      };

                updateLogoBlock(blockId, payload);
                const response = await onUploadNewFile(file, 'logos');
                URL.revokeObjectURL(blobUrl);
                setActivePanel(null);
                if (response) {
                  const payload =
                    type === 'org'
                      ? {
                          orgLogoEnabled: true,
                          selectedOrgLogo: response.url,
                          customOrgLogos: [response.url, ...logoListToRender],
                        }
                      : {
                          recipientLogoEnabled: true,
                          selectedRecipientLogo: response.url,
                          customRecipientLogos: [response.url, ...logoListToRender],
                        };
                  onLogoBlockUpdated(payload);

                  if (type === 'recipient' && recipientDomain) {
                    apiAddProspectLogo(recipientDomain, response.url);
                  }
                }
              }}
              accept={ACCEPTED_IMAGE_MIME_TYPES.join(',')}
            />
          </>
        ) : (
          <LogoFetcher
            onFetchSuccess={(sources) => {
              if (type && sources.length > 0) {
                const [firstLogo] = sources;
                const payload =
                  type === 'org'
                    ? {
                        orgLogoEnabled: true,
                        selectedOrgLogo: firstLogo,
                        availableOrgLogos: [...sources],
                      }
                    : {
                        recipientLogoEnabled: true,
                        selectedRecipientLogo: firstLogo,
                        availableRecipientLogos: [...sources],
                      };

                onLogoBlockUpdated(payload);
              }
              scrollListUp();
            }}
            onEnterUrlRef={onEnterUrlRef}
          />
        )}
        {logoListToRender.length ? (
          <div className='space-y-px max-h-[13.5rem] h-full overflow-auto'>
            <div ref={firstElementRef} className='h-px bg-transparent' />
            {logoListToRender.map((src, index) => {
              const isChecked = [selectedOrgLogo, selectedRecipientLogo].includes(src);
              return (
                <div
                  className={classNames('relative h-12 p-4 last:rounded-b-lg bg-neue-journey-fg-10 cursor-pointer', {
                    'rounded-t-lg': index === 0,
                  })}
                  onClick={() => {
                    if (!isChecked) {
                      const payload =
                        type === 'org'
                          ? {
                              orgLogoEnabled: true,
                              selectedOrgLogo: src,
                            }
                          : { recipientLogoEnabled: true, selectedRecipientLogo: src };
                      onLogoBlockUpdated(payload);
                    }
                  }}
                >
                  {renderLogoPreview(src, isChecked)}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};
